// useActiveSession — client heartbeat against active_sessions.
//
// V1 anti-sharing: every signed-in user pings claim_active_session every 5 min.
// Pro = 1 device. Elite = 2. Free = unlimited (no enforcement).
// When `allowed=false`, the consumer surfaces a soft banner — no hard block,
// because hard locks would punish honest users with multi-device usage.

import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const DEVICE_KEY = "dz_device_hash";

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try { return (crypto as any).randomUUID(); } catch { /* noop */ }
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getDeviceHash(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    let v = localStorage.getItem(DEVICE_KEY);
    if (!v) { v = uuid(); localStorage.setItem(DEVICE_KEY, v); }
    return v;
  } catch { return uuid(); }
}

export interface ActiveSessionState {
  allowed: boolean;
  activeCount: number;
  planLimit: number;
  currentPlan: string;
}

export function useActiveSession(): ActiveSessionState | null {
  const { user } = useAuth();
  const [state, setState] = useState<ActiveSessionState | null>(null);

  useEffect(() => {
    if (!user) { setState(null); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const beat = async () => {
      try {
        const { data, error } = await supabase.rpc("claim_active_session", {
          p_device_hash: getDeviceHash(),
          p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 200) : null,
        });
        if (cancelled || error || !data || !Array.isArray(data) || data.length === 0) return;
        const row = data[0] as any;
        setState({
          allowed: !!row.allowed,
          activeCount: Number(row.active_count ?? 0),
          planLimit: Number(row.plan_limit ?? 0),
          currentPlan: String(row.current_plan ?? "free"),
        });
      } catch { /* never break the app on telemetry */ }
    };

    void beat();
    timer = setInterval(beat, 5 * 60 * 1000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [user?.id]);

  return state;
}
